import type { FormGroup } from '@angular/forms';
import { DropdownData, Input, Value } from './input.interface';
import type { ReactiveChangeOrigin } from '../utils/change-origin/reactive-change-origin';
import type { ReactiveFieldDependency } from '../utils/dependencies/reactive-dependency-graph';
import type { ReactiveFormSerializerDefinition } from './reactive-form-serializer.interface';

export type FormButtonType = 'callback' | 'routerLink' | 'externalLink';


export interface ReactiveDropdownDataSourceContext<T = unknown> {
    readonly field: FormField<T>;
    readonly model: FormGroup | null;
    readonly previousData: T | null;
    readonly changedField: string | null;
    readonly changeOrigin: ReactiveChangeOrigin;
    readonly selectedRawValues: Readonly<Record<string, unknown | null>>;
    readonly getSelectedRaw: <TEntity = unknown>(fieldName: string) => TEntity | null;
    readonly getSelectedLabel: (fieldName: string) => string;
    readonly getSelectedBackendValue: (fieldName: string) => unknown | null;
}

export type ReactiveDropdownDataSourceParams<T = unknown> =
    Record<string, unknown> |
    ((context: ReactiveDropdownDataSourceContext<T>) => Record<string, unknown>);

export type ReactiveDropdownResponseMapper<T = unknown> = (
    response: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
) => DropdownData<unknown> | Value<unknown>[] | unknown[];

export type ReactiveDropdownOptionMapper<T = unknown> = (
    item: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
) => Value<unknown>;

export type ReactiveDropdownOptionFilter<T = unknown> = (
    option: Value<unknown>,
    context: ReactiveDropdownDataSourceContext<T>,
) => boolean;

export type ReactiveDropdownFallbackValueMapper<T = unknown> = (
    item: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
) => unknown;

export type ReactiveDropdownKeySelector<T = unknown> =
    string |
    string[] |
    ((item: unknown, context: ReactiveDropdownDataSourceContext<T>) => unknown);

export interface ReactiveDropdownRetryConfiguration {
    attempts?: number;
    delayMs?: number;
}

export interface ReactiveDropdownDataSource<T = unknown> {
    endpoint: unknown;
    cacheKey: string;
    lazy?: boolean;
    dependsOn?: readonly string[];
    params?: ReactiveDropdownDataSourceParams<T>;
    ttlMs?: number;
    timeoutMs?: number;
    retry?: ReactiveDropdownRetryConfiguration;
    forceReload?: boolean;
    cacheEmptyResults?: boolean;
    responsePath?: string;
    labelKey?: string;
    labelKeys?: readonly string[];
    valueKey?: string;
    valueKeys?: readonly string[];
    fallbackValue?: ReactiveDropdownFallbackValueMapper<T>;
    distinctBy?: ReactiveDropdownKeySelector<T>;
    mapResponse?: ReactiveDropdownResponseMapper<T>;
    mapOption?: ReactiveDropdownOptionMapper<T>;
    filterOption?: ReactiveDropdownOptionFilter<T>;
}

export interface ReactiveDropdownDependencyBehaviour {
    clearOnParentChange?: boolean;
    /** Clears the dependent control for these origins before falling back to the global origin policy. */
    clearOnOrigins?: readonly ReactiveChangeOrigin[];
    /** Preserves the dependent control for these origins even when the global policy would clear it. */
    preserveOnOrigins?: readonly ReactiveChangeOrigin[];
    /** Keeps the child value while dependencies are being hydrated from existing data. */
    clearOnHydration?: boolean;
    /** Keeps a saved value when the loaded option catalog no longer contains it. */
    preserveValueWhenOptionsMissing?: boolean;
    /** Shows a field warning when the saved value cannot be resolved against the loaded catalog. */
    warnWhenSelectedValueMissing?: boolean;
    autoSelect?: 'singleOption' | false;
    keepValueUntilOptionsLoaded?: boolean;
    reloadOnParentChange?: boolean;
    filterLocally?: boolean;
}

export interface FormButton {
    type?: FormButtonType;
    label: string;
    icon?: string;
    class?: string;
    style?: string | Record<string, string>;
    isVisible?: boolean;
    function?: () => void;
    route?: Array<string | number>;
    queryParams?: Record<string, unknown>;
    fragment?: string;
    href?: string;
    target?: string;
    rel?: string;
    tooltip?: string;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
    tooltipEvent?: 'hover' | 'focus';
}

export type FormSignatureUploadStrategy = 'taskFile' | 'deferredBase64';

export interface FormSignatureExistingImageConfiguration {
    idField?: string;
    pathPrefix: string;
    extension?: string;
}

export interface FormSignatureConfiguration {
    database?: string;
    folder: string;
    field?: string;
    uploadStrategy?: FormSignatureUploadStrategy;
    existingImageConfiguration?: FormSignatureExistingImageConfiguration;
}


export interface FormUploadButton {
    isEnabled: boolean;
    label: string;
    imageName?: string;
    imageFolder?: string;
    imageDisplay?: 'inline' | 'link' | 'header';
    accept?: string;
    allowedExtensions?: string[];
    invalidFormatMessage?: string;
    uploadUrl?: string;
    viewFolder?: string;
    showPreview?: boolean;
    maxFileSizeBytes?: number;
    idField?: string;
}

export interface Form<T> {
    name: string,
    header: string,
    type: FormType,
    sections: FormSection<T>[],
    table: FormTable | null,
    uploadButton: FormUploadButton,
    uploadButtons?: FormUploadButton[],
    /**
     * Header buttons rendered next to the upload button.
     * `button` is kept for backward compatibility with legacy forms.
     */
    button?: FormButton | FormButton[],
    buttons?: FormButton[],
    signed: boolean,
    signatureConfiguration?: FormSignatureConfiguration,
    /** Declarative backend payload serializer. */
    serializer?: ReactiveFormSerializerDefinition,
    /** Normalizes data loaded for edit/duplicate mode before patching the Angular model. */
    previousDataTransform?: (previousData: T) => T,
    /** Explicit dependency graph. Legacy field-level dependencies are adapted automatically. */
    dependencies?: readonly ReactiveFieldDependency[]
}

export interface FormSection<T> {
    name: string,
    fields: FormField<T>[],
    buttons?: FormButton[],
    tables?: FormTable[],
    width: number,
    isVisible: boolean,
    order: number
}

export interface ReactiveFieldLogicContext<T = unknown> {
    readonly field: FormField<T>;
    readonly model: FormGroup;
    readonly changedField: string | null;
    readonly changeOrigin: ReactiveChangeOrigin;
    readonly currentValue: unknown;
    readonly getValue: (fieldName: string) => unknown;
    readonly getRawValue: () => Record<string, unknown>;
}

export interface ReactiveComputedFieldContext<T = unknown> {
    readonly field: FormField<T>;
    readonly model: FormGroup;
    readonly currentValue: unknown;
    readonly getValue: (fieldName: string) => unknown;
    readonly getRawValue: () => Record<string, unknown>;
}

export interface ReactiveComputedField<T = unknown> {
    readonly dependsOn?: readonly string[];
    readonly emitEvent?: boolean;
    readonly resolve: (context: ReactiveComputedFieldContext<T>) => unknown;
}

export interface FormField<T> {
    [key: string]:
        ((it: FormField<T>) => T | string | null | undefined) |
        ((it: FormField<T>) => void) |
        Input<T> |
        FormFieldConditions |
        string[] |
        string |
        number |
        boolean |
        FormButton[] |
        any |
        undefined;
    name: string;
    vinculatedFields: string[] | undefined;
    label: string;
    input: Input<T>;
    dataSource?: ReactiveDropdownDataSource<T>;
    dependencyBehaviour?: ReactiveDropdownDependencyBehaviour;
    /** Declarative calculated value. Prefer this over new `updateValue` usages. */
    computed?: ReactiveComputedField<T>;
    /** Explicit dependencies owned by this field. Prefer this over new `vinculatedFields` usages. */
    dependencies?: readonly ReactiveFieldDependency[];
    checkForDuplicates?: (it: string) => boolean;
    width: number,
    conditions: FormFieldConditions;
    buttons?: FormButton[],
    order: number;
    turnVisible?: (it: FormField<T>, context?: ReactiveFieldLogicContext<T>) => void;
    updateValue?: (it: FormField<T>, context?: ReactiveFieldLogicContext<T>) => T | string | null | undefined;
}

export interface FormTable {
    title: string,
    showIndex: boolean,
    data: string[] | string[][] | undefined,
    style: string[] | string[][] | undefined
}

export interface FormFieldConditions {
    isRememberedOnDuplicate: boolean;
    isRequired: boolean;
    isRetained: boolean;
    hasUndoEnabled: boolean;
    isVisible: boolean;
}

export enum FormType {
    CREATION,
    EDITION,
    DUPLICATION,
    DELETION,
    INFORMATIVE,
    EXPORT,
    GENERATION
}
